import { RefObject, useCallback, useEffect, useRef } from "react";
import { Platform } from "react-native";

import { CloseMenu } from "../types";

type UseMenuKeyboardNavigationArgs = {
  closeMenu: CloseMenu;
  isOpen: boolean;
  menuContentRef: RefObject<unknown>;
};

const getEnabledMenuItems = (menuContent: HTMLElement) =>
  Array.from(
    menuContent.querySelectorAll<HTMLElement>('[role="menuitem"]')
  ).filter(
    (item) =>
      item.getAttribute("aria-disabled") !== "true" &&
      !item.hasAttribute("disabled")
  );

/**
 * Restores the focus and directional-key behavior expected from web menus.
 */
export const useMenuKeyboardNavigation = ({
  closeMenu,
  isOpen,
  menuContentRef,
}: UseMenuKeyboardNavigationArgs) => {
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const closeMenuAndRestoreFocus = useCallback<CloseMenu>(
    (afterClose) => {
      closeMenu(() => {
        triggerElementRef.current?.focus();
        afterClose?.();
      });
    },
    [closeMenu]
  );

  useEffect(() => {
    if (Platform.OS !== "web" || !isOpen || typeof document === "undefined") {
      return undefined;
    }

    const menuContent = menuContentRef.current;
    if (!(menuContent instanceof HTMLElement)) {
      return undefined;
    }

    const activeElement = document.activeElement;
    triggerElementRef.current =
      activeElement instanceof HTMLElement ? activeElement : null;

    const focusTimer = setTimeout(() => {
      getEnabledMenuItems(menuContent)[0]?.focus();
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenuAndRestoreFocus();
        return;
      }

      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
        return;
      }

      const menuItems = getEnabledMenuItems(menuContent);
      if (menuItems.length === 0) {
        return;
      }

      event.preventDefault();
      const focusedElement = document.activeElement;
      const focusedIndex = menuItems.findIndex(
        (item) => item === focusedElement || item.contains(focusedElement)
      );
      const offset = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        focusedIndex === -1
          ? offset === 1
            ? 0
            : menuItems.length - 1
          : (focusedIndex + offset + menuItems.length) % menuItems.length;
      menuItems[nextIndex]?.focus();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMenuAndRestoreFocus, isOpen, menuContentRef]);

  return closeMenuAndRestoreFocus;
};
